import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { EditMap, setEditMapData } from 'components';

import {
  createMapStyle,
  getDataFromProps,
  fetchBoundariesShapes,
  fetchMapDatasetGeojson,
  withEmptyDatapointsGeojson,
} from './utils';

const MapContainer = (props) => {
  const { visualizationType, aggregates = [] } = props;
  const [mapData, setMapData] = useState(null);
  const [boundariesShapes, setBoundariesShapes] = useState(null);
  const hasVisualizedAggregate = aggregates.some(({ isVisualized }) => isVisualized);
  const visualizedAggregate = aggregates.find(({ isVisualized }) => isVisualized);
  const effectiveVisualizationType = visualizationType === 'CHOROPLETH' && !hasVisualizedAggregate
    ? 'LOCATION'
    : visualizationType;

  const {
    basemap,
    heatmapGradient,
    shapeBorderColor,
    shapeBorderWidth,
    mapDatasets,
    mapShapes,
    color,
  } = getDataFromProps(props);
  const [hydratedMapDatasets, setHydratedMapDatasets] = useState(
    () => withEmptyDatapointsGeojson(mapDatasets),
  );

  const mapShapesUrls = mapShapes.map(({ shape }) => shape.geojsonUrl);
  const mapShapesUrlsKey = mapShapesUrls.join('|');
  const mapShapeSelectionKey = mapShapes
    .map(({ id, shape }) => `${id}:${shape.id}`)
    .join('|');
  const mapDatasetsKey = mapDatasets
    .map(
      ({ id, datapointsGeojsonUrl, dataset }) => (
        `${id}:${dataset && dataset.id ? dataset.id : ''}:${datapointsGeojsonUrl || ''}`
      ),
    )
    .join('|');
  const datasetHydrationKey = `${mapDatasetsKey}::${mapShapeSelectionKey}`;

  useEffect(() => {
    // Keep the latest map-dataset metadata in sync without throwing away already-fetched
    // GeoJSON unless the underlying dataset/source actually changed.
    setHydratedMapDatasets(currentHydratedMapDatasets => mapDatasets.map((mapDataset) => {
      const matchingHydratedMapDataset = currentHydratedMapDatasets.find(
        ({ id }) => id === mapDataset.id,
      );
      const sameDataset = matchingHydratedMapDataset
        && matchingHydratedMapDataset.dataset
        && mapDataset.dataset
        && matchingHydratedMapDataset.dataset.id === mapDataset.dataset.id;
      const sameGeojsonSource = matchingHydratedMapDataset
        && matchingHydratedMapDataset.datapointsGeojsonUrl === mapDataset.datapointsGeojsonUrl;

      return {
        ...mapDataset,
        datapointsGeojson: sameDataset && sameGeojsonSource
          ? matchingHydratedMapDataset.datapointsGeojson
          : (mapDataset.datapointsGeojson || { type: 'FeatureCollection', features: [] }),
      };
    }));
  }, [mapDatasets]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const fetchedBoundariesShapes = await fetchBoundariesShapes(mapShapesUrls);

      if (!cancelled) {
        setBoundariesShapes(fetchedBoundariesShapes);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mapShapesUrlsKey]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Boundary selection changes the filtered datapoint set, so dataset GeoJSON must be
      // refetched when either the dataset source or the active map-shape selection changes.
      const datasetsWithGeojson = await fetchMapDatasetGeojson(mapDatasets);

      if (!cancelled) {
        setHydratedMapDatasets(datasetsWithGeojson);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [datasetHydrationKey]);

  useEffect(() => {
    if (boundariesShapes === null) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const map = await createMapStyle(props);

      const mapStyles = await setEditMapData(map, [hydratedMapDatasets, boundariesShapes], {
        lineColor: shapeBorderColor,
        lineWidth: shapeBorderWidth,
        visualizationType: effectiveVisualizationType,
        choroplethAggregate: visualizedAggregate,
        heatmapGradient,
        mapShapes,
      });

      if (!cancelled) {
        setMapData(mapStyles);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    boundariesShapes,
    hydratedMapDatasets,
    basemap,
    shapeBorderColor,
    shapeBorderWidth,
    effectiveVisualizationType,
    visualizedAggregate,
    heatmapGradient,
    mapShapes,
  ]);

  return mapData ? (
    <EditMap
      mapStyles={mapData}
      color={color}
      {...props}
      mapDatasets={hydratedMapDatasets}
      visualizationType={effectiveVisualizationType}
    />
  ) : null;
};

MapContainer.propTypes = {
  aggregates: PropTypes.array,
  visualizationType: PropTypes.string.isRequired,
};

export default MapContainer;
